import React, { useState } from "react";
import { Autocomplete, Box, TextField, Typography, useTheme } from "@mui/material";
import SelectedChipsArea from "./SelectedChipsArea";
import FilterPanelActions from "./FilterPanelActions";
import { fieldSx, panelLabelSx } from "./filterPanelStyles";

const nestedListProps = {
  componentsProps: {
    popper: { disablePortal: true },
  },
  ListboxProps: {
    style: { maxHeight: 220, padding: 4 },
  },
};

function commitTermValue(currentValues, pending) {
  const next = String(pending ?? "").trim();
  if (!next) return null;
  const existing = Array.isArray(currentValues) ? currentValues : [];
  if (existing.some((term) => String(term).toLowerCase() === next.toLowerCase())) {
    return existing;
  }
  return [...existing, next];
}

function TermsField({
  id,
  value,
  onChange,
  disabled,
  autoFocus = false,
  placeholder = "Add a term",
  compactSelectedChips = false,
  onApply,
}) {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");
  const selectedValues = Array.isArray(value) ? value : [];

  const commitPending = () => {
    const nextValues = commitTermValue(selectedValues, inputValue);
    if (nextValues) onChange(nextValues);
    setInputValue("");
  };

  if (compactSelectedChips) {
    return (
      <SelectedChipsArea
        data-testid={id ? `${id}-selected-chips` : "selected-chips-area"}
        values={selectedValues}
        onRemove={(term) =>
          onChange(selectedValues.filter((item) => String(item) !== String(term)))
        }
        onClear={() => onChange([])}
        disabled={disabled}
        inputId={id}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onInputBlur={commitPending}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onInputKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            commitPending();
            onApply?.();
          }
        }}
      />
    );
  }

  return (
    <Autocomplete
      id={id}
      multiple
      freeSolo
      options={[]}
      value={selectedValues}
      inputValue={inputValue}
      onInputChange={(_event, next, reason) => {
        if (reason === "reset") return;
        setInputValue(next);
      }}
      onChange={(_event, next) => {
        onChange(next);
        setInputValue("");
      }}
      onBlur={commitPending}
      disabled={disabled}
      clearOnBlur={false}
      {...nestedListProps}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          variant="outlined"
          placeholder={placeholder}
          sx={fieldSx(theme)}
          autoFocus={autoFocus}
          onBlur={(event) => {
            params.inputProps?.onBlur?.(event);
            commitPending();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              event.stopPropagation();
              commitPending();
            }
          }}
        />
      )}
    />
  );
}

function TermsPanel({
  termsInclude,
  setTermsInclude,
  termsExclude,
  setTermsExclude,
  disabled,
  onApply,
}) {
  const theme = useTheme();
  const hasValues = Boolean(termsInclude?.length || termsExclude?.length);
  const includeLabel = "Include companies matching these exact terms";
  const excludeLabel = "Exclude companies matching these exact terms";

  return (
    <Box
      sx={{
        opacity: disabled ? 0.45 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <Typography
        component="p"
        sx={{
          ...panelLabelSx(theme),
          mb: 0.5,
          display: "flex",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 0.5,
        }}
      >
        {includeLabel}
      </Typography>
      <TermsField
        id="v2-terms-include-autocomplete"
        value={termsInclude}
        onChange={setTermsInclude}
        disabled={disabled}
        autoFocus
        compactSelectedChips
        onApply={onApply}
      />
      <Typography
        component="p"
        sx={{
          ...panelLabelSx(theme),
          mt: 1.25,
          mb: 0.5,
          display: "flex",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 0.5,
        }}
      >
        {excludeLabel}
      </Typography>
      <TermsField
        id="v2-terms-exclude-autocomplete"
        value={termsExclude}
        onChange={setTermsExclude}
        disabled={disabled}
        compactSelectedChips
        onApply={onApply}
      />
      <FilterPanelActions
        clearDisabled={!hasValues || disabled}
        onClear={() => {
          setTermsInclude([]);
          setTermsExclude([]);
        }}
        onApply={onApply}
      />
    </Box>
  );
}

export { TermsPanel, TermsField, nestedListProps, commitTermValue };
